import { QueryKey } from '@tanstack/react-query';
import omit from 'lodash/omit';
import { queryClient } from 'api/reactQuery';
import { put, get, remove } from 'utils/storage';

/**
 * Persist a single react query in local storage by id
 * @example
 * Storage: \{\}
 * Input: id=1, queryKey=['mobility-services', 'account-1234'], data=[...]
 * Storage: \{ queries: \{'1': \{ queryKey: ['mobility-services', 'account-1234'], data: [...] \}\} \}
 *
 * @param id       - Identifier to use to store this specific query key and data
 * @param queryKey - Query key to identify query with react-query
 * @param data     - Data to store to be able to restore this data as the response
 *                   of the specified queryKey
 */
export const writeOneToStorage = (id: string, queryKey: QueryKey, data: any) => {
  const storageData = get('queries') ?? {};
  const updatedStorageData = {
    ...storageData,
    [id]: {
      queryKey,
      data
    }
  };
  put('queries', updatedStorageData);
};

/**
 * Same as writeOneToStorage but allow to store the same query results if it
 * has a different queryKey to identify it
 * @param queryKey - Query key to identify query with react-query
 * @param data     - Data to store
 */
export const writeOneWithKeyToStorage = (queryKey: QueryKey, data: any) => {
  const storageData = get('queries') ?? {};
  const updatedStorageData = {
    ...storageData,
    [JSON.stringify(queryKey)]: {
      queryKey,
      data
    }
  };
  put('queries', updatedStorageData);
};

/**
 * Hydrate all react queries from localStorage and set them as initial query data
 * with react-query 'queryCache.setQueryData(queryKey, data)'.
 *
 * Side effects:
 *   1. Set queries data with react-query
 *   2. Remove all queries from the local storage
 */
export const readAllFromStorage = () => {
  const queriesWithData = get('queries');
  if (queriesWithData !== null) {
    const queryKeys = Object.keys(queriesWithData);
    queryKeys.forEach(id => {
      const query = queriesWithData[id];
      queryClient.setQueryData(query.queryKey, query.data);
    });
    remove('queries');
  }
};

/**
 * Hydrate a specific react query from localStorage
 * with react-query 'queryCache.setQueryData(queryKey, data)'.
 *
 * Side effects:
 *   1. Set the specified query data with react-query
 *   2. Remove this specific query only from the local storage
 *
 * * @param id - Identifier to use to get a specific query key and data
 */
export const readOneFromStorage = (id: string | QueryKey) => {
  const queriesWithData = get('queries');
  const idStr = typeof id === 'string' ? id : JSON.stringify(id);
  const query = queriesWithData?.[idStr];
  if (query) {
    queryClient.setQueryData(query.queryKey, query.data);
    const updatedStorageData = omit(queriesWithData, idStr);
    put('queries', updatedStorageData);
  }
};